import type { FormikErrors } from 'formik';
import { isArray, isEmpty, set } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useRouter } from './useRouter';

export const useValidation = <TFormValues extends any>() => {
  const { translate } = useRouter();

  const requiredMessage = useMemo(() => {
    return translate('common.validation.required');
  }, [translate]);

  const emailMessage = useMemo(() => {
    return translate('common.validation.email');
  }, [translate]);

  const passwordDifferentMessage = useMemo(() => {
    return translate('common.validation.passwordsError');
  }, [translate]);

  const invalidMessage = useMemo(() => {
    return translate('common.validation.in').replace("'%s' ", '');
  }, [translate]);

  const maxPriceMessage = useMemo(() => {
    return translate('common.validation.maxNumeric').replace("'%s' ", '');
  }, [translate]);

  const minPriceMessage = useMemo(() => {
    return translate('common.validation.minNumeric').replace("'%s' ", '');
  }, [translate]);

  const fileTooLargeMessage = useMemo(() => {
    return translate('common.validation.fileTooBig');
  }, [translate]);

  const noBreedingWithoutPedigree = useMemo(() => {
    return translate('common.validation.noBreedingWithoutPedigree');
  }, [translate]);

  const minCharactersMessage = useCallback(
    (min: number) => {
      return translate('common.validation.atLeast').replace('$min', min.toString());
    },
    [translate],
  );

  const diffAvailabilityBirth = useMemo(() => {
    return translate('common.validation.diffAvailabilityBirth');
  }, [translate]);

  const invalidMicrochip = useMemo(() => {
    return translate('common.validation.invalidShip');
  }, [translate]);

  const litterInvalidPedigree = useMemo(() => {
    return translate('common.validation.invalidPedigree');
  }, [translate]);

  const containsSensitiveDataMessage = useMemo(() => {
    return translate('common.validation.sensitiveData');
  }, [translate]);

  const mapSchemaToObject = useCallback((map, schema) => {
    // https://github.com/jquense/yup/issues/130
    return Object.keys(map).reduce((newMap, key) => ({ ...newMap, [key]: schema }), {});
  }, []);

  const isValidMicrochip = useCallback((value: string) => {
    const regex = new RegExp(
      /^([0-9]{15}|[0-9]{9}A|[0-9a-zA-Z]{10}|[a-zA-Z]{6}|B[0-9]{3}[a-zA-Z]{2}|[0-9]{3}[a-zA-Z]{3}|VO[0-9]{4}|W[0-9a-zA-Z]{6})$/,
    );
    if (!!value && !value?.match(regex)) return false;
    return true;
  }, []);

  const isValidMobilePhone = useCallback((value: string) => {
    const regex = new RegExp(/^((\+?)([0-9]){8,13})$/);
    if (!!value && !value?.match(regex)) return false;
    return true;
  }, []);

  const errorsForKeys = useCallback(
    (values: TFormValues, keys: Array<keyof TFormValues>): FormikErrors<TFormValues> => {
      let errors: FormikErrors<TFormValues> = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const key of keys) {
        const falsy = isArray(values[key]) ? isEmpty(values[key]) : !values[key];
        if (falsy) {
          errors = set(key, requiredMessage, errors);
        }
      }
      return errors;
    },
    [requiredMessage],
  );

  const siretMessageFR = useMemo(() => {
    return translate('common.validation.agreementNumber.france');
  }, [translate]);

  const agreementMessageNL = useMemo(() => {
    return translate('common.validation.agreementNumber.nederland');
  }, [translate]);

  const doesNotContainSensitiveData = useCallback((value: string) => {
    if (!value) {
      return true;
    }

    const emailRegex = new RegExp(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);
    if (emailRegex.test(value)) {
      return false;
    }

    const phoneRegex = new RegExp(/(?:[-+()/. ]*\d){9,13}/gm);
    if (phoneRegex.test(value)) {
      return false;
    }

    const urlRegex = new RegExp(
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi,
    );
    if (urlRegex.test(value)) {
      return false;
    }
    return true;
  }, []);

  const imageIsRequired = useMemo(() => {
    return translate('common.validation.imageRequired');
  }, [translate]);

  const isValidEmail = (email: string | null | undefined) => {
    const isValid = String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
    return !!isValid;
  };
  const isValidUrl = (url: string | null | undefined) => {
    if (!url) return false;
    const pattern = new RegExp(
      '^https?:\\/\\/' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+' + // domain name
        '[a-z]{2,}|' + // OR a domain extension like .com
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IPv4 address
        '(\\:\\d+)?' + // optional port
        '(\\/[-a-z\\d%_.~+]*)*' + // path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i', // fragment locator
    );
    return !!pattern.test(url);
  };
  return {
    isValidMobilePhone,
    requiredMessage,
    noBreedingWithoutPedigree,
    emailMessage,
    passwordDifferentMessage,
    invalidMessage,
    mapSchemaToObject,
    minCharactersMessage,
    diffAvailabilityBirth,
    isValidMicrochip,
    invalidMicrochip,
    minPriceMessage,
    maxPriceMessage,
    fileTooLargeMessage,
    errorsForKeys,
    siretMessageFR,
    agreementMessageNL,
    doesNotContainSensitiveData,
    containsSensitiveDataMessage,
    imageIsRequired,
    litterInvalidPedigree,
    isValidEmail,
    isValidUrl,
  };
};
